import './styles/tokens.css';
import './styles/base.css';
import './styles/public.css';
import './styles/admin.css';
import { createRouter, type Router } from './router.js';
import { authStore } from './state/auth.js';
import { homePage } from './pages/home.js';
import { newsDetailPage } from './pages/news-detail.js';
import { contactsPage } from './pages/contacts.js';
import { boardPage } from './pages/board.js';
import { loginPage } from './pages/login.js';
import { busPage } from './pages/bus.js';

const app = document.getElementById('app');
if (!app) throw new Error('App element not found');

const router = createRouter(app);

// Public routes
router.add('/', () => homePage());
router.add('/news/:id', (params) => newsDetailPage(params));
router.add('/contacts', () => contactsPage());
router.add('/board', () => boardPage());
router.add('/bus', () => busPage());
router.add('/login', () => loginPage());

// Helper: check if user is authorized for admin routes
function requireAuth(routerInstance: Router): void {
  const { user, isLoading } = authStore.getState();
  if (isLoading) return; // Will re-check after auth loads
  
  if (!user) {
    routerInstance.navigate('/login');
  }
}

// Admin routes (dynamic imports for code splitting)
router.add('/admin', async () => {
  requireAuth(router);
  const mod = await import('./pages/admin/root.js');
  return mod.adminRootPage(router, 'news');
});

router.add('/admin/news', async () => {
  requireAuth(router);
  const mod = await import('./pages/admin/root.js');
  return mod.adminRootPage(router, 'news');
});

router.add('/admin/contacts', async () => {
  requireAuth(router);
  const mod = await import('./pages/admin/root.js');
  return mod.adminRootPage(router, 'contacts');
});

router.add('/admin/listings', async () => {
  requireAuth(router);
  const mod = await import('./pages/admin/root.js');
  return mod.adminRootPage(router, 'listings');
});

router.add('/admin/audit', async () => {
  requireAuth(router);
  const mod = await import('./pages/admin/root.js');
  return mod.adminRootPage(router, 'audit');
});

router.add('/admin/news/new', async () => {
  requireAuth(router);
  const mod = await import('./pages/admin/news-edit.js');
  return mod.newsEditPage(router);
});

router.add('/admin/news/:id', async (params) => {
  requireAuth(router);
  const mod = await import('./pages/admin/news-edit.js');
  const routeParams: { id?: number } = {};
  if (params.id) routeParams.id = parseInt(params.id, 10);
  return mod.newsEditPage(router, routeParams);
});

router.add('/admin/contacts/new', async () => {
  requireAuth(router);
  const mod = await import('./pages/admin/contact-edit.js');
  return mod.contactEditPage(router);
});

router.add('/admin/contacts/:id', async (params) => {
  requireAuth(router);
  const mod = await import('./pages/admin/contact-edit.js');
  const routeParams: { id?: number } = {};
  if (params.id) routeParams.id = parseInt(params.id, 10);
  return mod.contactEditPage(router, routeParams);
});

// Check auth on load and wait for result before routing to admin
let authCheckComplete = false;
let unsubscribe: (() => void) | null = null;

function handleAuthChange(): void {
  if (authCheckComplete) return;
  
  const { isLoading } = authStore.getState();
  
  // Wait for auth check to complete
  if (isLoading) return;
  
  authCheckComplete = true;
  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
  
  // Resolve current route
  router.resolve();
}

unsubscribe = authStore.subscribe(handleAuthChange);
authStore.checkAuth();
